import { AuthContextUserData } from 'contexts/AWSCognitoContext.tsx';
import { ReactElement } from 'react';
import { UserData } from './user.ts';

// ==============================|| AUTH TYPES  ||============================== //

export type GuardProps = {
    children: ReactElement | null;
};

export type UserProfile = {
    id?: string;
    email?: string;
    avatar?: string;
    image?: string;
    name?: string;
    role?: string;
    tier?: string;
};

/**
 * Outcome of handing a Cognito session to the backend. `mfaSetupRequired` means the password was
 * accepted but the tenant demands an authenticator enrolment first; no portal session exists yet.
 */
export type BackendAuthStatus = 'authenticated' | 'mfaSetupRequired';

export type AuthContextType = {
    isLoggedIn: boolean;
    isInitialized?: boolean;
    cognitoUserProfile?: UserProfile | null;
    user?: AuthContextUserData | null | undefined;
    logout: () => void;
    login: (email: string, password: string) => Promise<void>;
    azureLogin: (email: string, jwt: string) => Promise<void>;
    register: (email: string, password: string, firstName: string, lastName: string) => Promise<unknown>;
    sendResetPasswordCode: (email: string) => Promise<void>;
    resetPassword: (email: string, verificationCode: string, newPassword: string) => Promise<string>;
    updateProfile: VoidFunction;
    setInitialPassword: (newPassword: string) => Promise<BackendAuthStatus>;
    setupTOTP: () => Promise<string>;
    verifyTOTP: (code: string) => Promise<void>;
    completeMfaSetup: () => Promise<void>;
    sendOTP: (code: string) => Promise<void>;
    /**
     * Re-enters the password inside the portal to get a Cognito session for setting an authenticator up
     * from the profile. Rejects when Cognito already challenges the account for a code: that account is
     * enrolled, and replacing an authenticator is a support task.
     */
    reauthenticate: (password: string) => Promise<void>;
    discardCognitoSession: () => void;
    impersonateUser: (userData: UserData) => void;
    /** Applies a fresh read of the same session user: what changed is what they may do or what is on for them. */
    refreshSessionUser: (userData: UserData) => void;
};

/** Thrown by `reauthenticate` when Cognito already challenges the account for a code. */
export class AlreadyEnrolledError extends Error {
    constructor() {
        super('An authenticator is already active for this account');
        this.name = 'AlreadyEnrolledError';
    }
}
